import { defineStore } from 'pinia';
import booknet from '@/network/booknet';
import basicapi from '@/network/optional';
import customercontactnet from '@/network/customercontactnet';
import { useAuthorityStore } from '@/store/authority';
import router from '@/router';
import { ElMessageBox, ElMessage } from 'element-plus';

const messageConfig = require('@/models/messageConfig.js');

// 定义并导出容器
export const useOrderStore = defineStore('OrderForm', {
  /**
   * 类似于组件的data,用户存储全局状态
   * - 必须是函数: 这样是为了在服务端渲染的时候避免交叉请求导致的数据状态污染
   */
  state: () => {
    return {
      // 订单编号
      OrderNumber: '',
      // 订单Id
      Id: 0,
      // 订单信息
      Order: {},
      // 由于定义Alert Message 为String进行转换
      AlertMessages: [],
      // 是否显示Alert Message
      isAlertMessage: false,
      BillNameId: '',
      // 当前订单的客户所属行业
      BusinessCategories: [],
      // 当前订单已选择的所属行业Id
      OrderBusinessCategoryIds: [],
      // 订单客户联系人
      ContactPersons: [],
      // 订单销售员
      Sales: {},
      // 订单销售历史
      SalesRecords: [],
      // 订单产品
      Product: {},
      // 订单产品所属的产品分类
      ProductCategoryList: [],
      // 订单产品所属分类Id
      ProductCategoryIds: [],
      // 订单合同
      Contracts: [],
      // 产品选项
      ProductCatOptionals: [],
      // 代理列表
      AgencyList: [],
      // 联系人选项
      ContactOptionals: [],
      // 账单
      BillNameOptionals: [],
      // 是否已产生分拆
      bHasIntlSplit: false,
      // 页面验证
      OrderRules: {
        salesman: {
          required: true,
          message: messageConfig.getMessage('validformrequire'),
          validation: true,
        },
        advertiserCode: {
          required: true,
          message: messageConfig.getMessage('validformrequire'),
          validation: true,
        },
        clientType: {
          required: true,
          message: messageConfig.getMessage('validformrequire'),
          validation: true,
        },
        agencyCode: {
          required: true,
          message: messageConfig.getMessage('validformrequire'),
          validation: true,
        },
        businessCategories: {
          message: messageConfig.getMessage('ordercategoryrequire'),
          validation: true,
        },
        ProductCategoryIds: {
          required: true,
          message: messageConfig.getMessage('validformrequire'),
          validation: true,
        },
        salesPermission: {
          required: true,
          message: messageConfig.getMessage('salesPermission2'),
          validation: true,
        },
        billName: {
          required: true,
          message: messageConfig.getMessage('validformrequire'),
          validation: true,
        },

        productName: {
          required: true,
          message: messageConfig.getMessage('validformrequire'),
          validation: true,
        },
        contacts: {
          required: true,
          message: messageConfig.getMessage('contactrequire'),
          validation: true,
        },
      },
      // 验证结果
      OrderValidationResult: [],
      // 销售列表
      SalesList: [],
      // 客户选项
      AdvertiserOptionals: [],
      AdvertiserOptionalsDC: [],
      AdvertiserOptionalsAG: [],
      AdvertiserOptionalsP: [],
      ReferralOptions: [],
      // 客户类型选项
      ClientTypeOptionals: [],
      // 组件可用状态
      disabled: false,
      // 页面模式
      mode: '',
      // 语言设置
      lang: 'en',
      // 行业分类选项
      BusinessOptions: [],
      //修改权限(作废 230817)
      //Bus_Form_Order_write: false,
      //审批权限(作废 230817)
      //Bus_Form_Order_approve: false,
      // bEnableEdit: false,
      // EnableEditMessage: "",
      // bEnableSave: false,
      // EnableSaveMessage: "",
      //bEnableNewContract: false,
      //EnableNewContractMessage: "",
      SelectProductCatOptionals: [],
      // bEnableChangeAction: false,
      // EnableChangeActionMessage: "",
      bEnableChangeHistory: false,
      //bEnableBulkApprove: false,
      OrderFunctions: [],
      Unit: {},
      SalesOption: [], // 销售员选项
      crmUrl: '',
    };
  },
  /**
   * 类似组件的computed,用来封装计算属性, 有缓存的功能
   */
  getters: {
    /**
     * 显示/隐藏作废按钮
     * @returns
     */
    isVoidButton() {
      return false;
    },
    /**
     * 显示/隐藏编辑按钮
     * @param {*} state
     * @returns
     */
    isEditButton(state) {
      return state.disabled;
    },
    /**
     * 显示/隐藏保存按钮
     * @param {*} state
     * @returns
     */
    isSaveButton(state) {
      return !state.disabled && state.Order.status !== 'Issued';
    },
    /**
     * 显示/隐藏客户信息
     * @param {*} state
     * @returns
     */
    isClientLabel(state) {
      return (state.Order.advertiserCode || '') !== '';
    },
    /**
     * 显示/隐藏代理信息
     * @param {*} state
     * @returns
     */
    isAgencyLabel(state) {
      return state.Order.isAgencyBR || false;
    },
    SalesmanDisabled(state) {
      return state.Order.status !== 'Open' || false;
    },
    BulkApproveDisabled(state) {
      return !state.Contracts.filter((t) => t.isApprove).length > 0;
    },
    bChangeDisabled(state) {
      return state.Contracts.filter((t) => t.status === 'Issued').length === 0;
    },
    OrderStatus(state) {
      return state.Order.status || 'Open';
    },
  },
  /**
   * 类似于组件methods,封装业务逻辑，修改state
   */
  actions: {
    /**
     * 获取Order Form数据
     */
    async getOrder(OrderNumber) {
      //初始化数据,防止出现旧数据显示
      this.InitData();

      const resultOrder = await booknet
        .getOrderInfo(OrderNumber, this.Unit.id)
        .then((res) => res)
        .catch((error) => error);

      //if (resultOrder.statusText !== "OK") {
      if (resultOrder.status !== 200) {
        return;
      }
      const res = resultOrder.data;

      //是否有查看这条order的权限,没有权限提示,并返回主页
      if (!res.isRead) {
        await ElMessageBox.alert('No permission.', 'Message', {
          confirmButtonText: 'OK',
        })
          .then(() => { })
          .catch(() => { });
        //跳转主页
        window.location = '/report';
        return;
      }

      this.Order = res.order;
      // this.bEnableEdit = res.bEnableEdit
      // this.EnableEditMessage = res.enableEditMessage
      // this.bEnableSave = res.bEnableSave
      // this.EnableSaveMessage = res.enableSaveMessage
      // this.bEnableNewContract = res.bEnableNewContract
      // this.EnableNewContractMessage = res.enableNewContractMessage
      // this.bEnableBulkApprove = res.bEnableBulkApprove
      // this.EnableBulkApproveMessage = res.enableBulkApproveMessage
      // this.bEnableChangeAction = res.bEnableChangeAction
      // this.EnableChangeActionMessage = res.enableChangeActionMessage
      this.bEnableChangeHistory = res.bEnableChangeHistory;
      this.OrderFunctions = res.orderFunctions || [];
      this.Unit = res.unit;
      this.crmUrl = res.crmUrl;

      //深拷贝数据
      var newObj = Object.assign({}, res.order);
      if (this.Order.id == 0) {
        console.log('this.Order.id', this.Order.id);
        this.Order.isClientPermission = true;
      }

      // 读取基本的Option
      const optionals = await booknet
        .getSalesOption(this.Unit)
        .then((res) => res)
        .catch((error) => error);
      //if (optionals.statusText === 'OK') {
      if (optionals.status === 200) {
        // 销售员选项
        this.SalesList = optionals.data.list;

        if (
          this.SalesList.filter(
            (t) => t.salesmanCode === res.sales.salesmanCode
          ).length === 0
        ) {
          this.SalesList.push({
            salesmanCode: res.sales.salesmanCode,
            salesman: res.sales.salesman,
            isOrder: true,
          });
        }
        this.SalesList = this.SalesList.sort();
        this.SalesOption = Array.from(
          new Set(
            this.SalesList.map((t) =>
              JSON.stringify({
                salesmanCode: t.salesmanCode,
                salesman: t.salesman,
              })
            )
          )
        ).map((t) => JSON.parse(t));
      }

      // 客户选项
      var result = await basicapi
        .getContractingPartyData('en')
        .then((res) => res)
        .catch((error) => error);
      //if (result.statusText === 'OK') {
      if (result.status === 200) {
        this.ClientTypeOptionals = result.data.clientTypeOptions;
        this.AdvertiserOptionalsP = result.data.personalOptions;
        this.AdvertiserOptionalsDC = result.data.advertiserOptions;
        this.AdvertiserOptionalsAG = result.data.agAdvertiserOptions;
        this.AdvertiserOptionals = result.data.advertiserOptions;
        this.ReferralOptions = result.data.referralOptions;
        if (res.order && res.order.clientType == 'P') {
          this.AdvertiserOptionals = this.AdvertiserOptionalsP;
        }
        if (res.order && res.order.clientType == 'AG') {
          this.AdvertiserOptionals = this.AdvertiserOptionalsAG;
        }

        this.AgencyList = result.data.agencyOptions;
      }

      const authorityStore = useAuthorityStore();
      //修改权限
      //this.Bus_Form_Order_write = authorityStore.GetAuthority('Bus_Form_Order_write') && res.isWrite;
      //审批权限
      this.Bus_Form_Order_approve = authorityStore.GetAuthority(
        'Form_Order_BB_approve'
      );

      this.BusinessCategories = res.businessCategories;
      if (this.BusinessCategories.length > 0) {
        this.OrderBusinessCategoryIds =
          this.BusinessCategories.map((t) => t.businessCategoryId) || [];
      }

      this.Sales = res.sales;

      await this.changeAdvertiser(false);
      if (this.Order.clientType === 'AG') {
        await this.getBillNameOptionals();
        this.getContactPersonList(this.Order.agencyCode);
      }

      if (this.ContactPersons.length === 0) {
        this.ContactPersons = res.contactPersons;
      } else {
        this.ContactPersons.map((t) => {
          if (t.id === 0) {
            const id = res.contactPersons.find((cp) => cp.code === t.code);
            t.id = id;
          }
        });
      }

      this.SalesRecords = res.salesRecords;
      this.Product = res.product;
      this.ProductCategoryList = res.productCategories;
      this.ProductCategoryList.map((t) => {
        t.status = 'Active';
      });

      this.ProductCategoryIds = this.ProductCategoryList.filter(
        (t) => t.status !== 'Inactive'
      ).map((t) => t.productCategoryId);

      this.Contracts = res.contracts;
      this.Id = res.order.id;
      this.OrderNumber = OrderNumber;
      this.AlertMessages = JSON.parse(res.order?.companyAlerts || '[]');
      this.isAlertMessage = this.AlertMessages.length > 0;

      this.bHasIntlSplit = res.bHasInternalSplit;

      // 重置验证
      this.OrderRules.salesman.validation = true;
      this.OrderRules.advertiserCode.validation = true;
      this.OrderRules.clientType.validation = true;
      this.OrderRules.agencyCode.validation = true;
      this.OrderRules.billName.validation = true;
      this.OrderRules.contacts.validation = true;
      this.OrderRules.productName.validation = true;

      this.Order.billNameId = newObj.billNameId;
      this.Order.billAddress = newObj.billAddress;
      this.Order.billName = newObj.billName;

      this.Order.advertiserCode = newObj.advertiserCode;
      this.Order.isClientKeyAcc = newObj.isClientKeyAcc;
      this.Order.isClientBR = newObj.isClientBR;
      this.Order.clientBRExpired = newObj.clientBRExpired;
      this.Order.isClientTax = newObj.isClientTax;
      this.Order.businessNature = newObj.businessNature;
      this.Order.advertiserName = newObj.advertiserName;

      this.Order.referralName = newObj.referralName;
      this.Order.agencyName = newObj.agencyName;
      this.Order.isAgencyBR = newObj.isAgencyBR;
      this.Order.agencyBRExpired = newObj.agencyBRExpired;

      //根据OrderBusinessCategoryIds选择筛选
      if (this.OrderBusinessCategoryIds.length == 1) {
        this.SelectProductCatOptionals = this.ProductCatOptionals.filter(
          (p) => {
            return (
              this.OrderBusinessCategoryIds.indexOf(
                p.buesinessCategoryLevelTwoId
              ) > -1
            );
          }
        );
      }
    },
    /**
     * 更新Order Alert Message
     * @param {Array} AlertMessages
     */
    changeAlertMessage(AlertMessages) {
      this.isAlertMessage = AlertMessages.length > 0;
      this.AlertMessages = AlertMessages;
      this.Order.companyAlerts = JSON.stringify(AlertMessages);
    },
    /**
     * 变更客户
     * @param {String} CustomerCode 客户编号
     */
    async changeAdvertiser(isrun) {
      console.log('changeAdvertiser', isrun);
      // if (!this.AdvertiserOptionals) {
      //   return;
      // }
      // this.Order.isClientPermission = true;
      //清空数据
      if (isrun) {
        if (this.Order.clientType === 'DC' || this.Order.clientType === 'P') {
          this.BusinessOptions = [];
          this.OrderBusinessCategoryIds = [];
          this.ProductCategoryIds = [];
          this.ProductCatOptionals = [];
          this.Order.isNewClient = false;
          this.Order.isClientPermission = true;
          this.Order.isClientKeyAcc = false;
          this.Order.businessNature = '';
          this.Order.advertiserName = '';
          this.Order.isClientTax = '';
          this.Order.isClientBR = false;
          this.Order.clientBRExpired = '';
          this.Order.billNameId = '';
          this.Order.billName = '';
          this.Order.billAddress = '';
          this.BillNameOptionals = [];
          this.Order.isAgencyDiscount = false;
          this.Order.agencyDiscount = 0;
          this.Order.agencyCode = null;
          this.Order.agencyName = null;
          this.Order.referralCode = null;
          this.Order.referralName = null;
          this.Order.agencyType = null;
          this.Order.isAgencyBR = null;
          this.Order.agencyBRExpired = null;
          this.ProductCategoryList = [];
          this.ContactPersons = [];
          // this.resetData();
        } else {
          this.Order.isNewClient = false;
          this.OrderBusinessCategoryIds = [];
          this.ProductCategoryIds = [];
          this.ProductCatOptionals = [];
          this.BusinessOptions = [];
          this.Order.isNewClient = false;
          this.Order.isClientPermission = true;
          this.Order.isClientKeyAcc = false;
          this.Order.businessNature = '';
          this.Order.advertiserName = '';
          this.Order.isClientTax = '';
          this.Order.isClientBR = false;
          this.Order.clientBRExpired = '';
          this.ProductCategoryList = [];
        }
      }
      //没有选择advertiser
      if (!this.Order.advertiserCode) {
        return;
      }
      // 更新客户基本信息
      const dataInfo = this.AdvertiserOptionals?.find(
        (p) => p.value == this.Order.advertiserCode
      );
      //this.Order.isClientPermission = true;
      this.Order.isClientKeyAcc = dataInfo?.keyacc || false;
      this.Order.businessNature = dataInfo?.naturename || '';
      this.Order.advertiserName = dataInfo?.label || '';
      this.Order.isClientTax = (dataInfo?.tax88Url || '') !== '';
      this.Order.isClientBR =
        dataInfo?.brExpiry != null && dataInfo?.brExpiry != '';
      this.Order.clientBRExpired = dataInfo?.brExpiry || '';

      // 更新行业选项
      var params = {
        companyId: this.Order.advertiserCode,
        orderId: this.Id,
        lang: 'en',
        Unit: this.Unit?.alias,
      };

      var BusinessInfo = await basicapi
        .getBusinessCategoriesList(params)
        .then((res) => res);

      // 行业分类选项
      this.BusinessOptions = BusinessInfo.data.businessCategories;
      // 产品分类选项
      this.ProductCatOptionals = BusinessInfo.data.productCategories;

      if (isrun && this.Sales.salesmanCode) {
        this.bcPermission();
      }

      if (isrun) {
        this.ProductCategoryIds =
          this.ProductCategoryList.length === 0
            ? []
            : this.ProductCategoryList.map((t) => t.productCategoryId) || [];
        if (
          this.OrderBusinessCategoryIds.length === 0 &&
          this.BusinessOptions.length > 0
        ) {
          this.OrderBusinessCategoryIds =
            this.BusinessOptions.length === 0
              ? []
              : this.BusinessOptions.map((t) => t.id) || [];
        }
      }

      if (this.Order.clientType === 'DC' || this.Order.clientType === 'P') {
        // 删除原来的联系人
        this.ContactPersons = [];

        // 联系人选项
        await this.getContactPersonList(this.Order.advertiserCode);

        // 更新AlertMessage
        this.changeAlertMessage(dataInfo?.messageList || []);

        // 更新Bill Name
        await this.getBillNameOptionals();
        this.Order.billNameId =
          this.BillNameOptionals.find((t) => t.value === this.Order.billName)
            ?.id || '';

        if (this.Order.billName === '') {
          this.changeDefaultBillName();
        }
      } else {
        // 更新代理列表选项
        //await this.getAgencyList();
        // 变更代理
        // await this.changeAgency();
      }
    },
    //处理salesman和Advertiser的权限
    bcPermission() {
      this.Order.isClientPermission = true;
      //是否为keyacc公司
      if (!this.Order.isClientKeyAcc) {
        //salesman是否有advertiser对应的businessCategories
        var listid = this.BusinessOptions.filter((p) => {
          return (
            this.Sales.salesmanBusinessCategoryList.indexOf(
              p.buesinessCategoryLevelTwoId.toUpperCase()
            ) > -1
          );
        });
        if (listid.length < 1) {
          this.Order.isClientPermission = false;
        }
        // this.BusinessOptions = listid;
      } else {
        const dataInfo = this.AdvertiserOptionals.find(
          (p) => p.value == this.Order.advertiserCode
        );

        //所以若是DC, 用戶Order的Advertiser選了一個公司是UsersKeyaccount表內的, 便看對應的user是否現在用戶
        //若是便如常讓用戶繼續
        if (this.Sales.usersKeyaccountList.indexOf(dataInfo.id) < 0) {
          this.Order.isClientPermission = false;
        }
      }
      var auth = useAuthorityStore();
      //Salesman/TeamHead角色判断是否有权限
      const isNoSalesAdmin =
        auth.bHasRole('BBSalesman') || auth.bHasRole('BBTeamHead');
      //没有权限
      if (
        !this.Order.isClientPermission &&
        (this.Order.clientType === 'DC' || this.Order.clientType === 'P') &&
        isNoSalesAdmin
      ) {
        this.ContactPersons = [];
      }
    },

    /**
     * 更改Advertiser重置数据
     */
    resetData() {
      //#region  advertiser数据
      this.Order.isNewClient = false;
      this.Order.isClientPermission = true;
      this.Order.isClientKeyAcc = false;
      this.Order.businessNature = '';
      this.Order.advertiserName = '';
      this.Order.isClientTax = '';
      this.Order.isClientBR = false;
      this.Order.clientBRExpired = '';
      //#endregion

      //#region
      this.Order.billNameId = '';
      this.Order.billName = '';
      this.Order.billAddress = '';
      this.BillNameOptionals = [];
      //#endregion

      //#region Agency
      this.Order.isAgencyDiscount = false;
      this.Order.agencyDiscount = 0;
      this.Order.agencyCode = null;
      this.Order.agencyName = null;
      this.Order.referralCode = null;
      this.Order.referralName = null;
      this.Order.agencyType = null;
      this.Order.isAgencyBR = null;
      this.Order.agencyBRExpired = null;
      //#endregion

      this.ProductCategoryList = [];
    },
    // /**
    //  * 更换客户类型
    //  */
    // async changeClientType (clientTypeOld) {
    //   //没有修改不处理
    //   if (!clientTypeOld) { return; }
    //   this.Order.advertiserCode = "";
    //   this.ContactPersons = [];
    //   this.resetData();

    //   console.log("this.clientTypeOld", clientTypeOld, this.Order.clientType);
    //   if (this.Order.clientType === "DC") {
    //     //AG=>DC
    //     if (clientTypeOld == "AG") {

    //       this.ContactPersons = [];
    //       //#region
    //       this.Order.billNameId = "";
    //       this.Order.billName = "";
    //       this.Order.billAddress = "";
    //       this.BillNameOptionals = [];
    //       //#endregion

    //     }
    //     //P=>DC
    //     else if (clientTypeOld == "P") {
    //       this.ContactPersons = [];
    //       //#region BusinessCategory
    //       this.BusinessOptions = [];
    //       this.OrderBusinessCategoryIds = [];
    //       //#endregion

    //       //#region  ProductCategory
    //       this.ProductCategoryIds = [];
    //       this.ProductCatOptionals = [];
    //       //#endregion

    //       //#region  advertiser
    //       this.Order.advertiserCode = "";
    //       this.Order.advertiserName = "";
    //       this.Order.isNewClient = false;
    //       this.Order.isClientPermission = true;
    //       this.Order.isClientKeyAcc = false;
    //       this.Order.businessNature = "";
    //       this.Order.isClientTax = "";
    //       this.Order.isClientBR = false;
    //       this.Order.clientBRExpired = "";
    //       //#endregion

    //       //#region Agency
    //       this.Order.isAgencyDiscount = false;
    //       this.Order.agencyDiscount = 0;
    //       this.Order.agencyCode = null;
    //       this.Order.agencyType = null;
    //       this.Order.isAgencyBR = null;
    //       this.Order.agencyBRExpired = null;
    //       //#endregion

    //       //#region
    //       this.Order.illNameId = "";
    //       this.Order.billName = "";
    //       this.Order.billAddress = "";
    //       this.BillNameOptionals = [];
    //       //#endregion
    //       this.ProductCategoryList = [];
    //     }
    //     this.AdvertiserOptionals = this.AdvertiserOptionalsDC;
    //     await this.changeAdvertiser(false);
    //   } else if (this.Order.clientType === "AG") {
    //     this.ContactPersons = [];
    //     //#region Agency
    //     this.Order.isAgencyDiscount = false;
    //     this.Order.agencyDiscount = 0;
    //     this.Order.agencyCode = null;
    //     this.Order.agencyType = null;
    //     this.Order.isAgencyBR = null;
    //     this.Order.agencyBRExpired = null;
    //     //#endregion

    //     //#region
    //     this.Order.billNameId = "";
    //     this.Order.billName = "";
    //     this.Order.billAddress = "";
    //     this.BillNameOptionals = [];
    //     //#endregion

    //     // 启用并勾选Agency Discount
    //     this.Order.isAgencyDiscount = true;
    //     this.Order.agencyDiscount = 15;
    //     this.AdvertiserOptionals = this.AdvertiserOptionalsAG;
    //     await this.changeAgency();
    //   }
    //   //个人客户
    //   else if (this.Order.clientType === "P") {
    //     //DC=>P
    //     if (clientTypeOld == "DC") {
    //       this.ContactPersons = [];
    //       //#region BusinessCategory
    //       this.BusinessOptions = [];
    //       this.OrderBusinessCategoryIds = [];
    //       //#endregion

    //       //#region  ProductCategory
    //       this.ProductCategoryIds = [];
    //       this.ProductCatOptionals = [];
    //       //#endregion

    //       //#region  advertiser
    //       this.Order.advertiserCode = "";
    //       this.Order.advertiserName = "";
    //       this.Order.isNewClient = false;
    //       this.Order.isClientPermission = true;
    //       this.Order.isClientKeyAcc = false;
    //       this.Order.businessNature = "";
    //       this.Order.isClientTax = "";
    //       this.Order.isClientBR = false;
    //       this.Order.clientBRExpired = "";
    //       //#endregion

    //       //#region Agency
    //       this.Order.isAgencyDiscount = false;
    //       this.Order.agencyDiscount = 0;
    //       this.Order.agencyCode = null;
    //       this.Order.agencyType = null;
    //       this.Order.isAgencyBR = null;
    //       this.Order.agencyBRExpired = null;
    //       //#endregion
    //       //#region
    //       this.Order.billNameId = "";
    //       this.Order.billName = "";
    //       this.Order.billAddress = "";
    //       this.BillNameOptionals = [];
    //       //#endregion
    //       this.ProductCategoryList = [];
    //     }
    //     //AG=>P
    //     else if (clientTypeOld == "AG") {

    //       this.ContactPersons = [];
    //       //#region
    //       this.Order.billNameId = "";
    //       this.Order.billName = "";
    //       this.Order.billAddress = "";
    //       this.BillNameOptionals = [];
    //       //#endregion
    //     }

    //     this.AdvertiserOptionals = this.AdvertiserOptionalsP;
    //     await this.changeAdvertiser(false);
    //   }
    // },
    /**
     * 更换客户类型
     */
    async changeClientType(clientTypeOld) {
      //没有修改不处理
      if (!clientTypeOld) {
        return;
      }

      this.ContactPersons = [];
      this.Order.billNameId = '';
      this.Order.billName = '';
      this.Order.billAddress = '';
      this.BillNameOptionals = [];

      // 刷新控件状态
      if (this.Order.clientType !== 'AG') {
        if (this.Order.clientType === 'P' || clientTypeOld === 'P') {
          //#region BusinessCategory
          this.BusinessOptions = [];
          this.OrderBusinessCategoryIds = [];
          //#endregion

          //#region  ProductCategory
          this.ProductCategoryIds = [];
          this.ProductCatOptionals = [];
          //#endregion

          //#region  advertiser
          this.Order.advertiserCode = '';
          this.Order.advertiserName = '';
          this.Order.isNewClient = false;
          this.Order.isClientPermission = true;
          this.Order.isClientKeyAcc = false;
          this.Order.businessNature = '';
          this.Order.isClientTax = '';
          this.Order.isClientBR = false;
          this.Order.clientBRExpired = '';
          //#endregion
        }

        //#region Agency
        this.Order.isAgencyDiscount = false;
        this.Order.agencyDiscount = 0;
        this.Order.agencyCode = null;
        this.Order.agencyName = null;
        this.Order.referralCode = null;
        this.Order.referralName = null;
        this.Order.agencyType = null;
        this.Order.isAgencyBR = null;
        this.Order.agencyBRExpired = null;
        //#endregion
      }

      if (this.Order.clientType === 'DC') {
        this.AdvertiserOptionals = this.AdvertiserOptionalsDC;
        await this.changeAdvertiser(false);
      } else if (this.Order.clientType === 'AG') {
        // 启用并勾选Agency Discount
        this.Order.isAgencyDiscount = true;
        this.Order.agencyDiscount = 15;
        this.AdvertiserOptionals = this.AdvertiserOptionalsAG;
        await this.changeAgency();
      }
      //个人客户
      else if (this.Order.clientType === 'P') {
        this.AdvertiserOptionals = this.AdvertiserOptionalsP;
        await this.changeAdvertiser(false);
      }
    },
    /**
     * 变更Referral
     */
    async changeReferral() {
      // 更新基本信息
      var dataInfo = this.AdvertiserOptionalsAG.find(
        (p) => p.value == this.Order.referralCode
      );
      this.Order.referralName = dataInfo?.label || '';
    },
    /**
     * 变更代理
     */
    async changeAgency() {
      // 更新基本信息
      var dataInfo = this.AgencyList.find(
        (p) => p.value == this.Order.agencyCode
      );
      this.Order.agencyType = dataInfo?.agencyTypesName || '';
      this.Order.agencyName = dataInfo?.label || '';
      this.Order.isAgencyBR =
        dataInfo?.brExpiry != null && dataInfo?.brExpiry != '';
      this.Order.agencyBRExpired = dataInfo?.brExpiry || '';

      this.Order.referralCode = null;
      this.Order.referralName = null;

      // 删除联系人
      this.ContactPersons = [];

      // 联系人选项
      await this.getContactPersonList(this.Order.agencyCode);

      // 更新AlertMessage
      this.changeAlertMessage(dataInfo?.messageList || []);

      // 更新账单
      await this.getBillNameOptionals();
      this.changeDefaultBillName();
    },
    /**
     * 代理列表
     */
    async getAgencyList() {
      var agency_res = [];
      if ((this.Order.advertiserCode || '') !== '') {
        agency_res = await basicapi
          .getAgencyListByAdvertiserId({
            companyId: this.Order.advertiserCode,
            lang: this.lang,
          })
          .then((res) => res.data);
      }
      this.AgencyList = agency_res;
    },
    /**
     * 联系人选项
     */
    async getContactPersonList(CustomerCode) {
      // 联系人选项
      var contactres = [];
      var auth = useAuthorityStore();

      if (
        auth.isOrderTop() ||
        this.Order.isClientPermission ||
        this.Order.clientType === 'AG'
      ) {
        if ((CustomerCode || '') !== '') {
          contactres = await customercontactnet
            .getCustomerContactsByCompanyId({
              CompanyId: CustomerCode,
              lang: 'en',
            })
            .then((res) => res.data);
        }
      }
      this.ContactOptionals = contactres;
      this.ContactOptionalsOld = contactres;
    },
    /**
     * 账单选项
     */
    async getBillNameOptionals() {
      var bill_res = [];

      var CompanyId =
        (this.Order.clientType === 'DC' || this.Order.clientType === 'P'
          ? this.Order.advertiserCode
          : this.Order.agencyCode) || '';

      if (CompanyId !== '') {
        bill_res = await basicapi
          .getBillNameOptinals({ companyId: CompanyId, lang: this.lang })
          .then((res) => res.data);
      }
      this.BillNameOptionals = bill_res;
    },
    /**
     * 默认账单
     */
    changeDefaultBillName() {
      const dataInfo =
        this.BillNameOptionals.length == 1
          ? this.BillNameOptionals[0]
          : this.BillNameOptionals?.find((t) => t.isDefault) || null;

      this.Order.billNameId = dataInfo?.id || '';
      this.Order.billName = dataInfo?.value || '';
      this.Order.billAddress =
        (dataInfo?.address1 || '') +
        '\r\n' +
        (dataInfo?.address2 || '') +
        '\r\n' +
        (dataInfo?.address3 || '');

      console.log('changeDefaultBillName', this.Order, dataInfo);
    },
    /**
     * 更换账单
     */
    changeBillName() {
      const dataInfo =
        this.BillNameOptionals?.find((t) => t.id === this.Order.billNameId) ||
        null;

      this.Order.billName = dataInfo?.value || '';
      this.Order.billAddress =
        (dataInfo?.address1 || '') +
        '\r\n' +
        (dataInfo?.address2 || '') +
        '\r\n' +
        (dataInfo?.address3 || '');
    },
    /**
     * 更新验证
     * @param {Object} OrderRules
     */
    VaidationOrder() {
      var auth = useAuthorityStore();
      //Salesman/TeamHead角色判断是否有权限
      const isNoSalesAdmin =
        auth.bHasRole('BBSalesman') || auth.bHasRole('BBTeamHead');

      this.OrderRules.salesman.validation = !(
        (this.Sales.salesman || '') === ''
      );
      if (!this.OrderRules.salesman.validation) {
        return this.OrderRules.salesman.message;
      }
      this.OrderRules.advertiserCode.validation = !(
        (this.Order.advertiserCode || '') === ''
      );
      if (!this.OrderRules.advertiserCode.validation) {
        return this.OrderRules.advertiserCode.message;
      }
      //BUS逻辑, Salesman/TeamHead角色登录选择了ag,但是没有权限
      if (this.getUnit()?.alias == 'Bus') {
        this.OrderRules.salesPermission.validation = !(
          this.Order.clientType === 'AG' &&
          isNoSalesAdmin &&
          !this.Order.isClientPermission
        );
      }
      this.OrderRules.salesPermission.validation = true; //初始化
      //salesman 登录,判断是否有bc权限
      if (
        isNoSalesAdmin &&
        !this.Order.isClientKeyAcc &&
        this.OrderBusinessCategoryIds.length == 1
      ) {
        var listid1 = this.OrderBusinessCategoryIds.filter((p) => {
          return (
            this.Sales.salesmanBusinessCategoryList.indexOf(p.toUpperCase()) >
            -1
          );
        });
        if (listid1.length < 1) {
          {
            this.OrderRules.salesPermission.validation = false;
          }
        }
      }
      if (!this.OrderRules.salesPermission.validation) {
        return this.OrderRules.salesPermission.message;
      }

      this.OrderRules.clientType.validation = !(
        (this.Order.clientType || '') === ''
      );
      if (!this.OrderRules.clientType.validation) {
        return this.OrderRules.clientType.message;
      }

      this.OrderRules.agencyCode.validation = !(
        this.Order.clientType === 'AG' && (this.Order.agencyCode || '') === ''
      );
      if (!this.OrderRules.agencyCode.validation) {
        return this.OrderRules.agencyCode.message;
      }

      this.OrderRules.billName.validation = !(
        this.Order.clientType != 'P' && (this.Order.billName || '') === ''
      );
      if (!this.OrderRules.billName.validation) {
        return this.OrderRules.billName.message;
      }

      this.OrderRules.contacts.validation = !(this.ContactPersons.length === 0);
      if (!this.OrderRules.contacts.validation) {
        return this.OrderRules.contacts.message;
      }

      this.OrderRules.productName.validation = !(
        (this.Product.productName || '') === ''
      );
      if (!this.OrderRules.productName.validation) {
        return this.OrderRules.productName.message;
      }

      this.OrderRules.businessCategories.validation =
        this.OrderBusinessCategoryIds.length === 1;
      if (!this.OrderRules.businessCategories.validation) {
        return this.OrderRules.businessCategories.message;
      }

      this.OrderRules.ProductCategoryIds.validation =
        this.ProductCategoryIds.length === 1;

      if (!this.OrderRules.ProductCategoryIds.validation) {
        return this.OrderRules.ProductCategoryIds.message;
      }
      // //若BusinessNature=Private, 在CRM找brurl=null或是brexpiry是已過期, 便要提示,'No BR for this client'
      // if (this.Order.clientType === 'DC' && this.Order.businessNature === 'Private' && (this.Order.clientBRExpired === '' || new Date(this.Order.clientBRExpired) < new Date())) {
      //   return "No BR for this client";
      // }
      // //若BusinessNature=Charity, 在CRM找tax88url=null, 便要提示,'No Tax88 for this client'
      // if (this.Order.clientType === 'DC' && this.Order.businessNature === 'Charity' && (this.Order.isClientTax === false)) {
      //   return "No Tax88 for this client";
      // }
      return '';
    },
    /**
     * 更新Salesman
     * @param {Object} Sales
     */
    changeSalesman() {
      const SalesGroup = this.SalesList.find(
        (t) => t.salesmanCode === this.Sales.salesmanCode
      );

      this.Sales.salesmanCode = SalesGroup.salesmanCode;
      this.Sales.salesman = SalesGroup.salesman;
      this.Sales.teamHeadCode = SalesGroup.teamHeadCode;
      this.Sales.teamHead = SalesGroup.teamHead;
      this.Sales.salesHeadCode = SalesGroup.salesHeadCode;
      this.Sales.salesHead = SalesGroup.salesHead;
      this.Sales.poolHeadCode = SalesGroup.poolHeadCode;
      this.Sales.poolHead = SalesGroup.poolHead;
      this.Sales.salesmanBusinessCategoryList =
        SalesGroup.salesmanBusinessCategoryList;
      this.Sales.salesmanBusinessCategory = SalesGroup.salesmanBusinessCategory;
      this.Sales.usersKeyaccountList = SalesGroup.usersKeyaccountList;
      this.Sales.departmentName = SalesGroup.departmentName;
      this.Sales.id = SalesGroup.id;
      // //清空数据
      // this.resetDCData();
      // this.AgencyList = [];
      // this.ContactPersons = [];
      // this.Product = {};
      // this.Order.clientType = "DC";
      // this.Order.advertiserCode = "";
      // this.Order.isNewClient = false;
      // this.Order.isAgencyDiscount = false;
      if (this.Order.advertiserCode) {
        this.bcPermission();
      }
    },
    changeProductCategory() {
      this.ProductCategoryList = [];
      this.ProductCategoryIds.map((t) => {
        const category =
          this.SelectProductCatOptionals.find((t2) => t2.id === t) || null;
        this.ProductCategoryList.push({
          id: 0,
          productCategoryId: category.id,
          code: category.code,
          name: category.name,
          orderProductNameId: this.Product.id || 0,
          status: '',
          sorting: this.ProductCategoryList.length,
        });
      });
    },
    changeProductCategoryBak() {
      // 移除的Category更新状态为Inactive
      const CatList = this.ProductCategoryList.filter(
        (t) => this.ProductCategoryIds.indexOf(t.id) < 0
      );
      if (CatList.length > 0) {
        CatList.map((t) => (t.status = 'Inactive'));
      }

      this.ProductCategoryIds.map((t) => {
        const entity =
          this.ProductCategoryList.find((pc) => pc.productCategoryId === t) ||
          null;

        if ((entity || null) !== null) {
          entity.status = entity.id !== 0 ? 'Active' : 'Add';
        } else {
          const category =
            this.ProductCatOptionals.find((t2) => t2.id === t) || null;
          if (category !== null) {
            this.ProductCategoryList.push({
              id: 0,
              productCategoryId: category.id,
              code: category.code,
              name: category.name,
              orderProductNameId: this.Product.id || 0,
              status: 'Add',
              sorting: this.ProductCategoryList.length,
            });
          }
        }
      });
    },
    /**
     * 删除联系人
     * @param {Number} index 索引
     */
    deleteContactPerson(index) {
      this.ContactPersons.splice(index, 1);
    },
    /**
     * 添加联系人
     * @param {Array} Contacts 联系人列表
     */
    addContactPerson() {
      const Contacts = this.ContactOptionals.filter((t) => t.isChecked);
      Contacts.map((t) => {
        const contact =
          this.ContactPersons.find((cp) => cp.code === t.id) || null;
        if (contact === null) {
          this.ContactPersons.push({
            id: 0,
            code: t.id.toString(),
            name: t.contactPerson,
            title: t.jobTitle,
            phone: t.contactTel,
            mobile: t.contactMobile,
            email: t.contactEmail,
            fax: t.contactFax,
            orderId: this.Id,
            owner: t.contactOwner,
          });
        }
      });
    },
    /**
     * 更新页面模式
     * @param {String} mode 模式:view,edit
     */
    changeMode(mode) {
      const routeParams = require('./routeParams')?.routeParamsStore();
      this.mode = mode;

      routeParams.setPageMode(mode);
      this.disabled = this.mode === 'view';
    },
    //设置是否有此数据操作权限
    async getDataAuthority(OrderNumber, Unit) {
      const res = await booknet
        .getOrderInfo(OrderNumber, Unit)
        .then((res) => res.data);
      //是否有查看这条order的权限,没有权限提示,并返回主页
      if (!res.isRead) {
        await ElMessageBox.alert('No permission.', 'Message', {
          confirmButtonText: 'OK',
        })
          .then(() => { })
          .catch(() => { });
        //跳转主页
        window.location = '/report';
        return;
      }
      const authorityStore = useAuthorityStore();
      //修改权限
      //this.Bus_Form_Order_write = authorityStore.GetAuthority('Bus_Form_Order_write') && res.isWrite;
      //审批权限
      this.Bus_Form_Order_approve = authorityStore.GetAuthority(
        'Form_Order_BB_approve'
      );
      this.OrderNumber = OrderNumber;
    },
    //初始化数据
    InitData() {
      // 订单信息
      this.Order = {};
      // 由于定义Alert Message 为String进行转换
      this.AlertMessages = [];
      // 是否显示Alert Message
      this.isAlertMessage = false;
      this.Order.billNameId = '';
      // 当前订单的客户所属行业
      this.BusinessCategories = [];
      // 当前订单已选择的所属行业Id
      this.OrderBusinessCategoryIds = [];
      // 订单客户联系人
      this.ContactPersons = [];
      // 订单销售员
      this.Sales = {};
      // 订单销售历史
      this.SalesRecords = [];
      // 订单产品
      this.Product = {};
      // 订单产品所属的产品分类
      this.ProductCategoryList = [];
      // 订单产品所属分类Id
      this.ProductCategoryIds = [];
      // 订单合同
      this.Contracts = [];
      // 产品选项
      this.ProductCatOptionals = [];
      // 代理列表
      this.AgencyList = [];
      // 联系人选项
      this.ContactOptionals = [];
      this.ContactOptionalsOld = [];
      // 账单
      this.BillNameOptionals = [];
      // 是否已产生分拆
      this.bHasIntlSplit = false;
      // 验证结果
      this.OrderValidationResult = [];
      // 销售列表
      this.SalesList = [];
      // 客户选项
      this.AdvertiserOptionals = [];
      // 客户类型选项
      this.ClientTypeOptionals = [];
      this.AdvertiserOptionalsDC = [];
      this.AdvertiserOptionalsDC = [];
      // 行业分类选项
      this.BusinessOptions = [];
      //修改权限
      //this.Bus_Form_Order_write = false;
      //审批权限
      //this.Bus_Form_Order_approve = false;
    },
    /**
     * 代理信息
     */
    async focusAgency() {
      // 更新基本信息
      var dataInfo = this.AgencyList.find(
        (p) => p.value == this.Order.agencyCode
      );
      this.Order.agencyType = dataInfo?.agencyTypesName || '';
      this.Order.agencyName = dataInfo?.label || '';
      this.Order.isAgencyBR =
        dataInfo?.brExpiry != null && dataInfo?.brExpiry != '';
      this.Order.agencyBRExpired = dataInfo?.brExpiry || '';
    },
    /**
     * 客户信息
     */
    async focusAdvertiser() {
      // 更新客户基本信息
      const dataInfo = this.AdvertiserOptionals?.find(
        (p) => p.value == this.Order.advertiserCode
      );
      this.Order.isClientKeyAcc = dataInfo?.keyacc || false;
      this.Order.businessNature = dataInfo?.naturename || '';
      this.Order.advertiserName = dataInfo?.label || '';
      this.Order.isClientTax = (dataInfo?.tax88Url || '') !== '';
      this.Order.isClientBR =
        dataInfo?.brExpiry != null && dataInfo?.brExpiry != '';
      this.Order.clientBRExpired = dataInfo?.brExpiry || '';
    },
    setEnableChangeHistory(value) {
      this.bEnableChangeHistory = value;
    },
    getState_Contracts() {
      return this.Contracts;
    },
    setState_Contracts(value) {
      this.Contracts = value;
    },
    getUnit() {
      return this.Unit;
    },
    /**
     * 更新Unit信息
     */
    setUnit(value) {
      this.Unit = value;
    },
    getSalesOption() {
      return this.SalesList?.map(
        (t) => new { value: t.salesmanCode, label: t.salesman }()
      );
    },
    getSalesList() {
      return [...this.SalesList];
    },
    /**
     * 权限获取
     * @param {*} alias 方法名
     * @returns
     */
    getOrderFunctionPermission(alias) {
      return this.OrderFunctions.find((t) => t.alias == alias);
    },
    getOrderData() {
      return { ...this.Order };
    },
    /**
     * 更新实体
     * @param {*} value
     */
    updateOrder(value) {
      this.Order = value;
    },
  },
  persist: {
    // 修改为 sessionStorage，默认为 localStorage
    storage: localStorage,
    // 部分持久化状态的点符号路径数组，[]意味着没有状态被持久化(默认为undefined，持久化整个状态)
    paths: ['OrderNumber', 'Form_Order_BB_write', 'Form_Order_BB_approve'],
  },
});
